import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Stack, 
  IconButton, 
  Link,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DiscordIcon, GithubIcon, InstagramIcon } from '../assets';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import acmAccLogo from '../assets/logos/acm-acc-logo-no-bg.svg';
import acmAccLogoDark from '../assets/logos/acm-acc-logo-no-bg-dark-mode.svg';
import { useThemeContext } from '../theme/ThemeContext';
import { useLocation } from 'react-router-dom';
import ThemeToggle from './common/ThemeToggle';

const SocialButton = styled(IconButton)(({ theme }) => ({
  width: '40px',
  height: '40px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '50%',
  padding: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
  '& img': {
    width: '24px',
    height: '24px',
    objectFit: 'contain',
    filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    '& img': {
      filter: 'brightness(0) invert(1)',
    },
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  transition: 'color 0.2s ease-in-out',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const Footer = () => {
  const { mode } = useThemeContext();
  const location = useLocation();
  const isRiverHackPage = location.pathname === '/riverhacks';
  const isDarkMode = mode === 'dark';

  const logo = isDarkMode ? acmAccLogoDark : acmAccLogo;

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        isolation: 'isolate',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        pt: 8,
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          {/* Logo Section */}
          <Grid item xs={12} md={4} lg={4}>
            <Box sx={{ mb: 3 }}>
              <img 
                src={logo}
                alt="ACM ACC Logo" 
                style={{ 
                  maxWidth: '180px',
                  height: 'auto'
                }} 
              />
            </Box>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Links
            </Typography>
            <Stack spacing={2}>
              <FooterLink href="/">Home</FooterLink>
              <FooterLink href="/about">About Us</FooterLink>
              <FooterLink href="/riverhacks">RiverHacks</FooterLink>
              <FooterLink href="/events">Events</FooterLink>
              <FooterLink href="/sponsor">Sponsor</FooterLink>
            </Stack>
          </Grid>

          {/* Contact Section */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Contact Us
            </Typography>
            <Stack spacing={2} color="text.secondary">
              <Typography>ACC ACM Student Chapter Department of Computer Information Technology</Typography>
              <Typography>6101 Highland Campus Dr, Austin, TX 78752</Typography>
              <FooterLink href="mailto:contact@austincs.org">
                contact@austincs.org
              </FooterLink>
            </Stack>
          </Grid>

          {/* Follow Us Section */}
          <Grid item xs={12} md={4} lg={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Follow Us
            </Typography>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2}>
                <Tooltip title="GitHub">
                  <Link
                    href="https://github.com/AustinCC-CSSO"
                    target="_blank"
                  >
                    <SocialButton>
                      <img src={GithubIcon} alt="GitHub" />
                    </SocialButton>
                  </Link>
                </Tooltip>
                <Tooltip title="Instagram">
                  <Link
                    href="https://www.instagram.com/acc.acm/"
                    target="_blank"
                  >
                    <SocialButton>
                      <img src={InstagramIcon} alt="Instagram" />
                    </SocialButton>
                  </Link>
                </Tooltip>
                <Tooltip title="Discord">
                  <Link
                    href="https://discord.gg/R3UMVDFbgV"
                    target="_blank"
                  >
                    <SocialButton>
                      <img src={DiscordIcon} alt="Discord" />
                    </SocialButton>
                  </Link>
                </Tooltip>
                <Tooltip title="LinkedIn">
                  <Link
                    href="https://www.linkedin.com/company/106521450"
                    target="_blank"
                  >
                    <SocialButton>
                      <LinkedInIcon />
                    </SocialButton>
                  </Link>
                </Tooltip>
                <Tooltip title="X (Twitter)">
                  <Link
                    href="https://x.com/acm_acc_101"
                    target="_blank"
                  >
                    <SocialButton>
                      <XIcon />
                    </SocialButton>
                  </Link>
                </Tooltip>
              </Stack>
              {!isRiverHackPage && (
                <Box>
                  <ThemeToggle />
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Copyright Section */}
        <Box
          sx={{
            mt: 6,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} ACM ACC. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
